import {
  ClipboardList,
  Users2,
  Wallet,
  CalendarClock,
  GanttChartSquare,
  GraduationCap,
  MessageSquare,
  BarChart3,
  Bus,
  Hotel,
  Library,
  HelpCircle,
  Heart,
  TrendingUp,
  Calculator,
  Banknote,
  ScrollText,
  ShieldCheck,
  type LucideIcon,
} from "lucide-react";

/**
 * Phase D0c — Frontend module catalog.
 *
 * The single mapping between BE module keys (from
 * `/me/dashboard/summary.enabled_modules`) and the FE chrome that
 * represents each module:
 *
 *   - sidebar / mobile-nav entries  (label, icon, route)
 *   - dashboard card titles
 *   - "Coming soon" page copy        (description, planned_phase)
 *
 * The BE catalog (eduviaos-backend/app/domains/tenant/modules.py) is
 * the SOURCE OF TRUTH for which modules a tenant CAN have. This file
 * is a presentation layer over those keys — adding a new key to the
 * BE without adding a row here means the dashboard simply won't
 * render a card for it (silent skip), which is the correct fail-safe.
 *
 * `phase` field maps to the EduViaOS phase plan slug so the
 * "Coming soon" page can show "Planned for Phase D2 (Fees)" instead
 * of an opaque "later" label.
 */

export interface ModuleDescriptor {
  key: string;
  label: string;
  icon: LucideIcon;
  route: string;
  /**
   * Short description shown on the "Coming soon" page when the module
   * is enabled but the BE data plane hasn't shipped yet.
   */
  description: string;
  /**
   * Planned phase slug ("D2", "D3", ...) for "Coming soon" copy. Null
   * when the module is already shipped (people, admissions in D0).
   */
  plannedPhase: string | null;
  /**
   * Module key whose page already covers this module. The module keeps
   * its BE toggle, but the sidebar shows one entry (the target's) and the
   * old route redirects there.
   */
  navMergedInto?: string;
}

/**
 * Curated dashboard / nav module descriptors. Order here is the
 * default presentation order in the sidebar — gating is applied on
 * top so a tenant with `enabled_modules = [people, admissions, fees]`
 * sees those three in this order.
 */
export const MODULE_CATALOG: ModuleDescriptor[] = [
  {
    key: "people",
    label: "People",
    icon: Users2,
    route: "/people",
    description:
      "Students, staff, parents, and guardians — manage rosters, profiles, attendance roll-up, and bulk actions.",
    plannedPhase: null,
  },
  {
    key: "learning",
    label: "Learning",
    icon: GraduationCap,
    route: "/learning",
    description:
      "Learner progress, study-group moderation, skill certificates, and reading-log oversight for your AI learning app.",
    plannedPhase: null,
  },
  {
    key: "admissions",
    label: "Admissions",
    icon: ClipboardList,
    route: "/admissions",
    description:
      "Enquiry funnel, applications, interviews, offers, and converted admissions — fully wired with vertical-aware funnel topology.",
    plannedPhase: null,
  },
  {
    key: "crm",
    label: "CRM",
    icon: TrendingUp,
    route: "/crm",
    description:
      "Lead and enquiry pipeline, follow-ups, and conversion tracking so no prospective learner slips away.",
    plannedPhase: null,
    navMergedInto: "admissions",
  },
  {
    key: "fees",
    label: "Fees",
    icon: Wallet,
    route: "/fees",
    description:
      "Fee plans, invoices, online payments via Razorpay, and partial payment tracking — sibling discount cascade, late-fee policy, GST capture.",
    plannedPhase: null,
  },
  {
    key: "attendance",
    label: "Attendance",
    icon: CalendarClock,
    route: "/attendance",
    description:
      "Daily attendance for students and staff, biometric / QR / manual capture, monthly heatmaps, and parent notifications.",
    plannedPhase: null,
  },
  {
    key: "timetable",
    label: "Timetable",
    icon: CalendarClock,
    route: "/timetable",
    description:
      "AI-powered weekly schedule generation, conflict detection, manual slot overrides, and publish/archive lifecycle.",
    plannedPhase: null,
  },
  {
    key: "exams",
    label: "Exams",
    icon: GanttChartSquare,
    route: "/exams",
    description:
      "Exam scheduling, hall arrangements, marks entry, grade computation, scorecards, and parent-facing report cards.",
    plannedPhase: "D6",
  },
  {
    key: "communications",
    label: "Communications",
    icon: MessageSquare,
    route: "/communications",
    description:
      "Email + SMS + WhatsApp templates, bulk announcements, scheduled sends, and per-template audit trail.",
    plannedPhase: null,
  },
  {
    key: "reports",
    label: "Reports",
    icon: BarChart3,
    route: "/reports",
    description:
      "Pre-built reports for admissions funnel, fees collection, attendance trends, and exam outcomes — all exportable.",
    plannedPhase: null,
  },
  {
    key: "transport",
    label: "Transport",
    icon: Bus,
    route: "/transport",
    description:
      "Routes, vehicles, drivers, student-to-route assignment, GPS pings (when wired), and parent live-bus visibility.",
    plannedPhase: "D9",
  },
  {
    key: "hostel",
    label: "Hostel",
    icon: Hotel,
    route: "/hostel",
    description:
      "Rooms, allocations, mess plans, leave requests, visitor logs, and warden dashboards.",
    plannedPhase: "D10",
  },
  {
    key: "library",
    label: "Library",
    icon: Library,
    route: "/library",
    description: "Catalogue, issue / return, fines, member portal, and reservation queue.",
    plannedPhase: "D11",
  },
  {
    key: "helpdesk",
    label: "Helpdesk",
    icon: HelpCircle,
    route: "/helpdesk",
    description:
      "Ticketing for parent + staff queries, SLAs, assignment rules, and resolution analytics.",
    plannedPhase: null,
  },
  {
    key: "accounting",
    label: "Accounting",
    icon: Calculator,
    route: "/accounting",
    description:
      "Ledgers, expenses, and gross / net profit — see fee income against every cost of running the centre.",
    plannedPhase: null,
  },
  {
    key: "payroll",
    label: "Team pay",
    icon: Banknote,
    route: "/team-pay",
    description:
      "Attendance, pay, advances, leave and pay runs — with team cost rolled into your profit picture.",
    plannedPhase: null,
  },
  {
    key: "audit_export",
    label: "Audit export",
    icon: ScrollText,
    route: "/audit-export",
    description:
      "A tamper-evident record of who did what, exportable for DPDPA and internal review.",
    plannedPhase: null,
  },
  {
    key: "dpdp_consent",
    label: "DPDPA consent",
    icon: ShieldCheck,
    route: "/dpdp-consent",
    description: "Capture and honour each learner's data-consent under India's DPDP Act.",
    plannedPhase: null,
  },
  {
    key: "parent_portal",
    label: "Parent Portal",
    icon: Heart,
    route: "/parent-portal",
    description:
      "Configure what parents see — fees, attendance, marks, calendar, communication preferences, and consent collection.",
    plannedPhase: "D7",
  },
];

/**
 * Lookup helper. Returns `undefined` for unknown keys so callers can
 * silently skip modules the FE doesn't have a descriptor for yet
 * (defensive against BE adding keys before the FE catches up).
 */
/**
 * Descriptors that get their own sidebar row. A merged module is skipped,
 * and its target is shown when either the target or any module merged
 * into it is enabled, so turning on CRM alone still surfaces Admissions.
 */
export function navModules(
  catalog: readonly ModuleDescriptor[],
  isEnabled: (key: string) => boolean,
): ModuleDescriptor[] {
  return catalog.filter((mod) => {
    if (mod.navMergedInto) return false;
    if (isEnabled(mod.key)) return true;
    return catalog.some((other) => other.navMergedInto === mod.key && isEnabled(other.key));
  });
}

export function moduleByKey(key: string): ModuleDescriptor | undefined {
  return MODULE_CATALOG.find((m) => m.key === key);
}

/**
 * Match a route path back to its module descriptor (for the
 * `ModuleComingSoonPage` that infers its content from the URL).
 * Tolerant of trailing slashes and nested paths under the module root.
 */
export function moduleByRoute(pathname: string): ModuleDescriptor | undefined {
  return MODULE_CATALOG.find((m) => pathname === m.route || pathname.startsWith(`${m.route}/`));
}
